import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import SchemaOrg from '@/components/base/SchemaOrg';
import { articles } from '@/mocks/homeData';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === Number(id));

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-file-unknow-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-foreground-500 mb-6">您要查看的文章不存在或已被移除</p>
            <Link
              to="/category"
              className="px-6 py-3 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              返回文章列表
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    datePublished: article.date,
    author: {
      '@type': 'Organization',
      name: 'j9官方平台',
    },
    publisher: {
      '@type': 'Organization',
      name: 'j9官方平台',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://j9guanwang.com/article/${article.id}`,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, item: { '@id': 'https://j9guanwang.com/', name: '首页' } },
      { '@type': 'ListItem', position: 2, item: { '@id': 'https://j9guanwang.com/category', name: '平台资讯' } },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  };

  const paragraphs = article.content.split('\n\n').filter(Boolean);

  return (
    <>
      <ScrollToTop />
      <SchemaOrg schema={articleSchema} />
      <SchemaOrg schema={breadcrumbSchema} />

      <Navbar />

      <main className="pt-16">
        <div className="bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6 pt-10 pb-8">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '平台资讯', to: '/category' },
                { label: article.title },
              ]}
            />
          </div>
        </div>

        <div className="bg-background-50 py-10 md:py-14 px-4 md:px-6">
          <article className="max-w-4xl mx-auto">
            <div className="mb-8">
              <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700 mb-4">
                {article.category}
              </span>
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center gap-3 text-sm text-foreground-500">
                <span>{article.date}</span>
                <span>·</span>
                <span>{article.readTime}阅读</span>
                <span>·</span>
                <span>j9官方平台</span>
              </div>
            </div>

            <div className="rounded-xl overflow-hidden mb-10">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-auto object-cover"
              />
            </div>

            <div className="prose-custom">
              {paragraphs.map((para, index) => {
                if (para.startsWith('## ')) {
                  return (
                    <h2 key={index} className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-10 mb-4">
                      {para.replace('## ', '')}
                    </h2>
                  );
                }
                return (
                  <p key={index} className="text-foreground-700 leading-relaxed mb-4 text-base">
                    {para}
                  </p>
                );
              })}
            </div>

            <div className="mt-12 pt-8 border-t border-background-200">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm text-foreground-500">标签：</span>
                {['j9', 'j9官网', 'j9平台', '综合娱乐平台'].map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1.5 rounded-full text-xs bg-background-100 text-foreground-600 border border-background-200"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-6 rounded-xl bg-background-100 border border-background-200/70">
              <div>
                <p className="text-sm font-medium text-foreground-900 mb-1">觉得文章有帮助？</p>
                <p className="text-xs text-foreground-500">立即注册j9平台，开启精彩娱乐之旅</p>
              </div>
              <a
                href="#"
                className="px-6 py-2.5 rounded-lg bg-accent-500 text-background-50 font-semibold text-sm hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                立即注册
              </a>
            </div>

            <div className="mt-8 flex items-center justify-between">
              <Link
                to="/category"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
              >
                <i className="ri-arrow-left-line"></i>
                返回文章列表
              </Link>

              <div className="flex items-center gap-2">
                {(() => {
                  const currentIndex = articles.findIndex((a) => a.id === article.id);
                  return (
                    <>
                      {currentIndex > 0 && (
                        <Link
                          to={`/article/${articles[currentIndex - 1].id}`}
                          className="px-4 py-2 rounded-lg text-sm text-foreground-600 bg-background-100 hover:bg-background-200 transition-colors whitespace-nowrap"
                        >
                          <i className="ri-arrow-left-s-line mr-1"></i>上一篇
                        </Link>
                      )}
                      {currentIndex < articles.length - 1 && (
                        <Link
                          to={`/article/${articles[currentIndex + 1].id}`}
                          className="px-4 py-2 rounded-lg text-sm text-foreground-600 bg-background-100 hover:bg-background-200 transition-colors whitespace-nowrap"
                        >
                          下一篇<i className="ri-arrow-right-s-line ml-1"></i>
                        </Link>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          </article>
        </div>
      </main>

      <Footer />
    </>
  );
}